<template>
    <div>
        <v-menu offset-y :close-on-content-click="closeOnClick" width="420px">
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                    class="mr-1"
                    @click="openNotif()"
                >
                    <v-badge
                        dense
                        :content="unreadCount"
                        :value="unreadCount"
                        color="error"
                        overlap
                    >
                        <v-icon>mdi-bell</v-icon>
                    </v-badge>
                </v-btn>
            </template>
            <v-card
                v-if="loading"
                width="350"
                class="mx-auto"
                style="max-height: 490px; overflow-y: auto"
            >
                <v-toolbar elevation="0" class="mt-5">
                    <v-toolbar-title>
                        <v-skeleton-loader
                            class="mt-5"
                            type="heading"
                            height="60"
                            width="250"
                        ></v-skeleton-loader>
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-skeleton-loader
                        type="avatar"
                        height="60"
                    ></v-skeleton-loader>
                    <template v-slot:extension>
                        <v-skeleton-loader
                            class="mr-3 mt-2"
                            type="button"
                            height="30"
                        ></v-skeleton-loader>
                        <v-skeleton-loader
                            class="mr-3 mt-2"
                            type="button"
                            height="30"
                        ></v-skeleton-loader>
                    </template>
                </v-toolbar>
                <v-skeleton-loader
                    class="ml-3 mt-5"
                    type="text"
                    height="30"
                    width="70"
                ></v-skeleton-loader>
                <div v-for="item in 4">
                    <v-skeleton-loader
                        v-bind="attrs"
                        type="list-item-avatar-three-line"
                    ></v-skeleton-loader>
                </div>
            </v-card>
            <v-card
                v-else
                width="350"
                class="mx-auto"
                style="max-height: 490px; overflow-y: auto"
                @scroll="handleScroll"
            >
                <v-toolbar v-if="showSettings" elevation="0">
                    <v-btn icon class="mr-2" @click="closeSettings">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-toolbar-title>Notification settings</v-toolbar-title>
                </v-toolbar>
                <v-toolbar v-else elevation="0">
                    <v-toolbar-title>Notifications</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mt-2"
                                color="primary"
                                fab
                                text
                                elevation="0"
                                @click="markAllAsRead"
                            >
                                <v-icon>mdi-email-open-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>Mark all as read</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mt-2"
                                color="primary"
                                fab
                                text
                                elevation="0"
                                @click="openSettings"
                            >
                                <v-icon>mdi-cog-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>Notification settings</span>
                    </v-tooltip>
                    <template v-slot:extension>
                        <v-btn
                            small
                            :rounded="activeButton === 'all'"
                            :text="activeButton !== 'all'"
                            class="mr-2"
                            :class="{ primary: activeButton === 'all' }"
                            @click="setActiveButton('all')"
                        >
                            All
                        </v-btn>
                        <v-btn
                            small
                            :rounded="activeButton === 'unread'"
                            :text="activeButton !== 'unread'"
                            class="mr-2"
                            :class="{ primary: activeButton === 'unread' }"
                            @click="setActiveButton('unread')"
                        >
                            Unread
                        </v-btn>
                    </template>
                </v-toolbar>
                <div v-if="showSettings">
                    <p class="caption px-4 mb-2">
                        Choose which notifications reach you. Anything switched
                        off is not sent at all — no push, no sound, and nothing
                        in this list.
                    </p>
                    <div
                        v-if="settingsFailed"
                        class="my-6 d-flex justify-center align-center"
                    >
                        <Failed :onFunctionComplete="fetchSettings"></Failed>
                    </div>
                    <div v-else-if="settingsLoading" class="px-4 pb-4">
                        <v-skeleton-loader
                            v-for="item in 4"
                            :key="item"
                            v-bind="attrs"
                            type="list-item-two-line"
                        ></v-skeleton-loader>
                    </div>
                    <v-list v-else class="py-0">
                        <template v-for="(type, index) in settingTypes">
                            <v-list-item :key="type.key">
                                <v-list-item-content>
                                    <v-list-item-title
                                        class="body-2"
                                        v-text="type.label"
                                    ></v-list-item-title>
                                    <!-- Pinuputol ng subtitle ang sarili nito
                                         sa isang linya kung hindi papayagang
                                         bumalot; mahaba ang mga paliwanag. -->
                                    <v-list-item-subtitle
                                        class="caption"
                                        style="white-space: normal"
                                        v-text="type.description"
                                    ></v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action class="ml-2 my-0">
                                    <v-tooltip bottom :disabled="!type.locked">
                                        <template v-slot:activator="{ on }">
                                            <!-- Hindi nagpapadala ng pointer
                                                 event ang naka-disable na
                                                 switch, kaya sa balot nakasabit
                                                 ang tooltip. -->
                                            <div v-on="on">
                                                <v-switch
                                                    v-model="settings[type.key]"
                                                    :disabled="
                                                        type.locked ||
                                                        savingSettings
                                                    "
                                                    class="mt-0"
                                                    dense
                                                    hide-details
                                                    @change="
                                                        saveSettings(type.key)
                                                    "
                                                ></v-switch>
                                            </div>
                                        </template>
                                        <span>
                                            Security alert — this one cannot be
                                            turned off.
                                        </span>
                                    </v-tooltip>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider
                                v-if="index < settingTypes.length - 1"
                                :key="type.key + '-divider'"
                            ></v-divider>
                        </template>
                    </v-list>
                </div>
                <div
                    v-else-if="loadingFailed"
                    class="my-10 d-flex justify-center align-center"
                >
                    <Failed :onFunctionComplete="fetchNotifs"></Failed>
                </div>
                <div v-else-if="formatted_notifs.length > 0">
                    <v-list two-line>
                        <div
                            v-for="(item, index) in formatted_notifs"
                            :key="index"
                        >
                            <v-subheader
                                v-if="item.isHeader"
                                v-text="item.header"
                            ></v-subheader>
                            <v-divider
                                v-else-if="item.isDivider"
                                :inset="item.inset"
                            ></v-divider>
                            <v-list-item
                                v-else
                                :style="item.read ? null : unreadStyle"
                                @click="markAsRead(item)"
                            >
                                <v-list-item-avatar>
                                    <v-avatar color="primary" size="40">
                                        <span
                                            class="white--text text-capitalize"
                                        >
                                            {{ item.name[0] }}
                                        </span>
                                    </v-avatar>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <p
                                        class="body-2"
                                        v-text="item.subtitle"
                                    ></p>
                                    <p class="ma-0 pa-0 caption">
                                        {{ item.created_at | relativeTime }}
                                    </p>
                                </v-list-item-content>
                            </v-list-item>
                        </div>
                        <div v-if="adding" v-for="item in 2">
                            <v-skeleton-loader
                                v-bind="attrs"
                                type="list-item-avatar-three-line"
                            ></v-skeleton-loader>
                        </div>
                        <div v-if="page >= last_page">
                            <p class="mt-5 text-center body-2">
                                No more notifications
                            </p>
                        </div>
                    </v-list>
                </div>
                <div v-else class="pa-4">
                    <p class="text-center body-2">You have no notifications</p>
                </div>
            </v-card>
        </v-menu>
    </div>
</template>
<script>
    import notificationSound from '../../../../public/sound/notif.mp3'
    import Failed from './../alert/failed.vue'

    export default {
        components: {
            Failed
        },

        data() {
            return {
                loadingFailed: false,

                attrs: {
                    boilerplate: true,
                    elevation: 0
                },
                closeOnClick: false,
                unreadCount: 0,
                loading: true,
                notificationAudio: new Audio(notificationSound),
                activeButton: 'all',
                notifs: [],
                unreadNotifs: [],
                formatted_notifs: [],
                adding: false,
                page: 1,
                last_page: '',

                // Ang settings ay pinapalitan lamang ang laman ng parehong
                // card, hindi isang bagong dialog: isang pindot ang layo nito
                // mula sa listahan, at isang pindot pabalik.
                showSettings: false,
                settingsLoading: false,
                settingsFailed: false,
                savingSettings: false,
                settingTypes: [],
                settings: {}
            }
        },

        computed: {
            // The unread highlight used to be Vuetify's `grey lighten-2`. A
            // palette class paints an opaque background and leaves the text
            // colour alone, so in night mode the theme's white text landed on a
            // light row and the message was unreadable.
            //
            // A translucent overlay that flips direction with the theme keeps
            // the surface underneath — and with it the theme's own text colour —
            // correct in both. Same mechanism as CallbackStatus.vue's
            // panelStyle().
            unreadStyle() {
                return {
                    backgroundColor: this.$vuetify.theme.dark
                        ? 'rgba(255, 255, 255, 0.10)'
                        : 'rgba(0, 0, 0, 0.06)'
                }
            }
        },

        methods: {
            markAllAsRead() {
                axios
                    .get('/mark-all-as-read')
                    .then((res) => {
                        if (this.activeButton == 'all') {
                            this.notifs.forEach((item) => {
                                item.read = true
                            })
                        } else {
                            this.unreadNotifications()
                        }
                    })
                    .catch((error) => {})
            },

            async markAsRead(selected_item) {
                let actualIndex = -1
                let items = []

                if (this.activeButton === 'all') {
                    items = this.notifs
                } else {
                    items = this.unreadNotifs
                }

                if (selected_item) {
                    if (this.activeButton === 'all') {
                        actualIndex = this.notifs.findIndex(
                            (item) => item.id === selected_item.id
                        )
                    } else {
                        actualIndex = this.unreadNotifs.findIndex(
                            (item) => item.id === selected_item.id
                        )
                    }

                    if (actualIndex !== -1) {
                        const resolved = this.$router.resolve(selected_item.link)
                        const isCatchAll = resolved.route.matched.some(r => r.path === '*')
                        if (!isCatchAll) {
                            this.$router.push(selected_item.link).catch(() => {})
                        } else {
                            window.open(selected_item.link, '_blank')
                        }
                        this.closeOnClick = true

                        if (!selected_item.read) {
                            try {
                                await axios.post('/mark-as-read', {
                                    id: selected_item.id
                                })
                                selected_item.read = true

                                // Refresh unread notifications list
                                await this.unreadNotifications()
                            } catch (error) {
                                console.error(
                                    'Error marking notification as read:',
                                    error
                                )
                            }
                        }

                        this.activeButton = 'all'
                    } else {
                        console.error(
                            'Notification not found at the provided index.'
                        )
                    }
                } else {
                    console.error('Selected item not found.')
                }
            },

            async setActiveButton(button) {
                this.activeButton = button
                this.loading = true

                if (this.activeButton == 'all') {
                    try {
                        const notificationListSuccess =
                            await this.notificationList()
                        if (notificationListSuccess) {
                            this.$nextTick(() => {
                                this.filteredItems()
                                this.loading = false
                            })
                        } else {
                            this.loading = false
                            // Handle unsuccessful fetch here if needed
                        }
                    } catch (error) {
                        this.loading = false
                        console.error(
                            'Error fetching notification list:',
                            error
                        )
                    }
                } else {
                    try {
                        const unreadNotificationsSuccess =
                            await this.unreadNotifications()
                        if (unreadNotificationsSuccess) {
                            this.$nextTick(() => {
                                this.filteredUnreadItems()
                                this.loading = false
                            })
                        } else {
                            this.loading = false
                            // Handle unsuccessful fetch here if needed
                        }
                    } catch (error) {
                        this.loading = false
                        console.error(
                            'Error fetching unread notifications:',
                            error
                        )
                    }
                }
            },

            openSettings() {
                this.showSettings = true
                this.fetchSettings()
            },

            closeSettings() {
                this.showSettings = false
            },

            async fetchSettings() {
                this.settingsLoading = true
                this.settingsFailed = false

                try {
                    const res = await axios.get('/notif-settings')
                    this.settingTypes = res.data.types
                    this.settings = res.data.settings
                } catch (error) {
                    this.settingsFailed = true
                } finally {
                    this.settingsLoading = false
                }
            },

            /**
             * Ang server ang nagbabalik ng huling kalagayan, kaya ito ang
             * ipinapatong pabalik sa mga switch. Nariyan din ang paraan ng
             * pagbawi kapag nabigo ang pagsulat: ibinabalik ang naunang halaga
             * imbes na iwang nagsisinungaling ang switch.
             */
            async saveSettings(changedKey) {
                this.savingSettings = true

                try {
                    const res = await axios.post('/notif-settings', {
                        settings: this.settings
                    })
                    this.settings = res.data.settings
                } catch (error) {
                    this.settings = {
                        ...this.settings,
                        [changedKey]: !this.settings[changedKey]
                    }
                } finally {
                    this.savingSettings = false
                }
            },

            handleScroll(event) {
                // Maikli ang settings kaysa sa card, kaya agad na totoo ang
                // pagsusuri sa ibaba — at magpapasimula iyon ng pagkuha ng
                // susunod na pahina ng listahang hindi naman nakikita.
                if (this.showSettings) {
                    return
                }

                const container = event.target
                if (
                    container.scrollTop + container.clientHeight >=
                    container.scrollHeight
                ) {
                    this.onScrollToBottom()
                }
            },

            onScrollToBottom() {
                this.adding = false
                if (this.page <= this.last_page) {
                    this.adding = true
                    this.page++
                    if (this.activeButton == 'all') {
                        axios
                            .post('/notif-list?page=' + this.page)
                            .then((res) => {
                                this.adding = false
                                this.notifs.push(...res.data.data)
                                this.last_page = res.data.last_page
                                this.filteredItems()
                            })
                            .catch((error) => {})
                    } else {
                        axios
                            .post('/unread-notifs?page=' + this.page)
                            .then((res) => {
                                this.adding = false
                                this.unreadNotifs.push(...res.data.data)
                                this.last_page = res.data.last_page
                                this.filteredUnreadItems()
                            })
                            .catch((error) => {})
                    }
                } else {
                    this.adding = false
                }
            },

            filteredUnreadItems() {
                const currentDate = new Date()
                const newItems = []
                const earlierItems = []

                // Filter only unread items
                this.unreadNotifs.forEach((item) => {
                    if (!item.read) {
                        const itemDate = new Date(item.created_at)
                        if (
                            itemDate >=
                            new Date(
                                currentDate.setDate(currentDate.getDate() - 1)
                            )
                        ) {
                            newItems.push(item)
                        } else {
                            earlierItems.push(item)
                        }
                    }
                })

                const result = []

                if (newItems.length > 0) {
                    result.push({ isHeader: true, header: 'New' })
                    result.push(...newItems)
                }

                if (earlierItems.length > 0) {
                    result.push({ isHeader: true, header: 'Earlier' })
                    result.push(...earlierItems)
                }

                this.formatted_notifs = result
            },

            filteredItems() {
                const currentDate = new Date()
                const newItems = []
                const earlierItems = []

                this.notifs.forEach((item) => {
                    const itemDate = new Date(item.created_at)
                    if (
                        itemDate >=
                        new Date(currentDate.setDate(currentDate.getDate() - 1))
                    ) {
                        newItems.push(item)
                    } else {
                        earlierItems.push(item)
                    }
                })

                const result = []

                if (newItems.length > 0) {
                    result.push({ isHeader: true, header: 'New' })
                    result.push(...newItems)
                }

                if (earlierItems.length > 0) {
                    result.push({ isHeader: true, header: 'Earlier' })
                    result.push(...earlierItems)
                }

                this.formatted_notifs = result
            },

            openNotif() {
                this.closeOnClick = false
                // Ang bell ay bumubukas palagi sa listahan. Ang settings ay
                // isang paglihis, hindi isang kalagayang dapat tandaan.
                this.showSettings = false
                this.fetchNotifs()
            },

            async fetchNotifs() {
                this.loading = true
                this.loadingFailed = false
                this.adding = false
                this.activeButton = 'all'

                try {
                    const notificationListSuccess =
                        await this.notificationList()
                    const unreadNotificationsSuccess =
                        await this.unreadNotifications()
                    if (notificationListSuccess && unreadNotificationsSuccess) {
                        this.$nextTick(() => {
                            this.filteredItems()
                            this.loading = false
                        })
                    } else {
                        this.loading = false
                        this.loadingFailed = true
                        // Handle unsuccessful fetch here if needed
                    }
                } catch (error) {
                    this.loading = false
                    this.loadingFailed = true
                    console.error('Error fetching notifications:', error)
                }
            },

            async unreadNotifications() {
                this.loading_list = true
                try {
                    this.formatted_notifs = []
                    this.page = 1
                    const res = await axios.post(
                        `/unread-notifs?page=${this.page}`
                    )
                    this.unreadCount = res.data.total
                    this.unreadNotifs = res.data.data
                    this.last_page = res.data.last_page
                    this.loading_list = false
                    return true
                } catch (error) {
                    console.error('Error fetching unread notifications:', error)
                    return false
                }
            },

            async notificationList() {
                this.loading_list = true
                try {
                    this.formatted_notifs = []
                    this.page = 1
                    const res = await axios.post(
                        `/notif-list?page=${this.page}`
                    )
                    this.notifs = res.data.data
                    this.last_page = res.data.last_page
                    this.loading_list = false
                    return true
                } catch (error) {
                    console.error('Error fetching notification list:', error)
                    return false
                }
            },

            playNotificationSound() {
                // Browsers block playback until the user interacts with the page.
                // Notifications arrive over the websocket without a user gesture,
                // so a rejection here is expected and safe to ignore.
                this.notificationAudio.play().catch(() => {})
            }
        },

        mounted() {
            this.fetchNotifs()

            Echo.channel('notification').listen('notify', (e) => {
                // Dating may hiwalay na pagsusuri dito sa permission para sa
                // report: ibinabalandra noon ng MergePdfJob ang toast nito nang
                // walang `receivers`. Dumadaan na iyon ngayon sa
                // notifyPermission(), kaya ang pagsusuri sa listahan sa ibaba
                // ang tanging bantay — at iisa ang sinusunod nito, ang parehong
                // salaan ng permission at ng switch ng user.
                const currentUserId = Number(window.awe[0])
                if (e.message.receivers && !e.message.receivers.map(Number).includes(currentUserId)) return

                const notif = {
                    ...e.message,
                    created_at: e.message.created_at || e.message.time,
                }

                this.playNotificationSound()
                this.unreadNotifs.unshift(notif)
                this.notifs.unshift(notif)

                if (this.activeButton == 'all') {
                    this.filteredItems()
                } else {
                    this.filteredUnreadItems()
                }

                this.unreadCount += 1
            })

            // Pribadong channel kada user: ito ang gamit ng mga abisong para
            // lang sa isang tao, gaya ng natapos na logs report.
            Echo.private(`notify-user.${window.awe}`).listen('.notify', (e) => {
                const notif = {
                    ...e.message,
                    created_at: e.message.created_at || e.message.time,
                }

                this.playNotificationSound()
                this.unreadNotifs.unshift(notif)
                this.notifs.unshift(notif)

                if (this.activeButton == 'all') {
                    this.filteredItems()
                } else {
                    this.filteredUnreadItems()
                }

                this.unreadCount += 1
            })

            Echo.private(`login.${window.awe}`).listen(
                '.login.attempt',
                (e) => {
                    this.playNotificationSound()
                    this.unreadNotifs.unshift(e.message)
                    this.notifs.unshift(e.message)

                    if (this.activeButton == 'all') {
                        this.filteredItems()
                    } else {
                        this.filteredUnreadItems()
                    }

                    this.unreadCount += 1
                }
            )
        }
    }
</script>
